/**
 * ConfirmRequiredBanner — amber banner shown above an unconfirmed
 * diagram-pushed Repairs row.
 *
 * Iter216 introduced the confirm-required flow for Iron repairs; Iter221
 * extracts it so all 3 Repairs builders can share the same banner inside
 * the new card-style rows.
 *
 * Two layout modes:
 *   - 'inline'  → compact pill that sits next to the F-chip (default)
 *   - 'banner'  → full-width amber strip across the top of the card
 *
 * Props:
 *   confirmed        — bool. When true the banner renders a small ✓ chip
 *                      with an "Unconfirm" tooltip so the operator can
 *                      walk back the confirm.
 *   onConfirm()      — confirm this row (push it into pricing)
 *   onUnconfirm()    — revert to pending
 *   testid           — prefix for the buttons
 *   variant          — 'inline' | 'banner'
 */
import React from 'react';

export default function ConfirmRequiredBanner({
  confirmed, onConfirm, onUnconfirm, testid = 'confirm-required', variant = 'inline',
}) {
  if (confirmed) {
    return (
      <button
        type="button"
        data-testid={`${testid}-unconfirm`}
        onClick={onUnconfirm}
        title="Confirmed — click to walk back. Row will revert to pending and stop pricing until re-confirmed."
        className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0 bg-emerald-100 hover:bg-amber-100 text-emerald-700 hover:text-amber-800 rounded-full font-bold uppercase tracking-wider ring-1 ring-emerald-300 transition-colors opacity-60 hover:opacity-100"
      >✓</button>
    );
  }
  if (variant === 'banner') {
    return (
      <div
        data-testid={`${testid}-banner`}
        role="alert"
        className="col-span-12 -mt-1 mb-1 bg-amber-50 border border-amber-300 text-amber-900 rounded px-2 py-1 flex items-center justify-between gap-2"
      >
        <span className="text-[11px] font-semibold">
          ⚠ Pushed from diagram — confirm before this row is added to the priced total.
        </span>
        <button
          type="button"
          data-testid={`${testid}-confirm`}
          onClick={onConfirm}
          className="text-[10px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold inline-flex items-center gap-1 shadow-sm"
        >
          ✓ Confirm to add to price
        </button>
      </div>
    );
  }
  // inline pill (default)
  return (
    <button
      type="button"
      data-testid={`${testid}-confirm`}
      onClick={onConfirm}
      title="Pushed from diagram. Click to confirm — until then the row is excluded from the priced total."
      className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0 bg-amber-400 hover:bg-emerald-600 hover:text-white text-amber-900 rounded-full font-bold uppercase tracking-wider ring-1 ring-amber-500 transition-colors"
    >⚠ Pending</button>
  );
}
